// #UbicacionUnify — Tab unificado de Mapa + Servicios + Emergencias
// Reemplaza los tabs antiguos "Mapa y Servicios" + "Servicios de Emergencia"
// tanto en LocationDetail como en EventDetail.
//
// Modos:
//   - mode="location"   → CRUD completo sobre nearby_services, map_points,
//                         emergency_services del venue (lider+ only)
//   - mode="event"      → vista consolidada: heredado del venue (read-only)
//                         + extras del evento (CRUD lider+)
//
// La data del backend viene en dos formas:
//   - location mode:    locationsAPI.getNearbyServices + getMapPoints +
//                       getEmergencyServices vía emergencyServicesAPI.list
//   - event mode:       eventsAPI.getMapAndServices (consolidado)
//
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MapPin, Plus, Trash2, Hospital, Fuel, Hotel, Landmark, UtensilsCrossed,
  Phone, ExternalLink, Search, X, Shield, Flame, AlertTriangle, ArrowRight,
  CheckCircle2, Building2, RefreshCw, Star, Maximize2, Minimize2, FileDown,
} from 'lucide-react';
// #OnePlanTaxonomy — picker con árbol de ~200 objetos OnePlan-style
import MapObjectPicker from './MapObjectPicker';
import { MAP_OBJECT_FLAT } from '../lib/mapObjectCategories';
// #MapIcons — pictogramas SVG estilo OnePlan para markers
import { buildMarkerIconUrl } from '../lib/mapIcons';

// #OnePlanTaxonomy — helper único de lookup: primero el flat de 200,
// luego legacy de 8, finalmente 'other'. Garantiza color/icon/label.
function getCategoryMeta(categoryId) {
  return MAP_OBJECT_FLAT[categoryId]
    || (categoryId && { id: categoryId, label: categoryId, color: '#94A3B8', icon: '📍' })
    || { id: 'other', label: 'Otro', color: '#94A3B8', icon: '📍' };
}
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  useJsApiLoader, GoogleMap, Marker, InfoWindow,
  Polygon, Polyline,
} from '@react-google-maps/api';
import { MAPS_KEY } from '../lib/locationConstants';
import { canFinishShape, dedupePath } from '../lib/mapDrawing';

// #MapShapes — ref estable para libraries del loader (evita reloads).
const GOOGLE_MAPS_LIBRARIES = ['drawing'];

// #MapShapes — defaults estilísticos por tipo (sobreescritos por shape.style)
const SHAPE_DEFAULTS = {
  polygon:  { fill_color: '#1A56DB', fill_opacity: 0.25, stroke_color: '#1A56DB', stroke_width: 2 },
  polyline: { stroke_color: '#0EA5E9', stroke_width: 3 },
};

// #MapShapes — formatea metric (m → m o km) para mostrar en InfoWindow
function fmtMeters(m) {
  if (m == null) return '—';
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${Math.round(m)} m`;
}
function fmtArea(m2) {
  if (m2 == null) return '—';
  if (m2 >= 10000) return `${(m2 / 10000).toFixed(2)} ha`;
  return `${Math.round(m2)} m²`;
}
import { useAuth } from '../context/AuthContext';
import { eventsAPI, locationsAPI, emergencyServicesAPI } from '../api/client';

// Categorías de map_points soportadas
const MAP_POINT_CATEGORIES = {
  entrance:       { label: 'Entrada',         color: '#15803D', icon: '🚪' },
  exit:           { label: 'Salida',          color: '#1A56DB', icon: '🚶' },
  evacuation:     { label: 'Evacuación',      color: '#0284C7', icon: '🏃' },
  assembly_point: { label: 'Punto reunión',   color: '#7C3AED', icon: '📍' },
  risk_zone:      { label: 'Zona de riesgo',  color: '#D97706', icon: '⚠️' },
  critical:       { label: 'Crítico',         color: '#DC2626', icon: '🔥' },
  facility:       { label: 'Instalación',     color: '#64748B', icon: '🏢' },
  hospital:       { label: 'Hospital',        color: '#DC2626', icon: '🏥' },
  vip_perimeter:  { label: 'Perímetro VIP',   color: '#7C3AED', icon: '⭐' },
  first_aid:      { label: 'Primeros auxilios', color: '#15803D', icon: '⛑️' },
  other:          { label: 'Otro',            color: '#64748B', icon: '📌' },
};

const NEARBY_CATEGORIES = [
  { id: 'hospital',    label: 'Hospitales',    icon: Hospital,        color: '#DC2626' },
  { id: 'gas_station', label: 'Gasolineras',   icon: Fuel,            color: '#D97706' },
  { id: 'hotel',       label: 'Hoteles',       icon: Hotel,           color: '#7C3AED' },
  { id: 'lodging',     label: 'Hospedaje',     icon: Hotel,           color: '#7C3AED' },
  { id: 'bank',        label: 'Bancos',        icon: Landmark,        color: '#1A56DB' },
  { id: 'restaurant',  label: 'Restaurantes',  icon: UtensilsCrossed, color: '#15803D' },
];

const EMERGENCY_ICONS = {
  hospital:         { icon: Hospital, color: '#1A56DB' },
  bomberos:         { icon: Flame,    color: '#DC2626' },
  policia:          { icon: Shield,   color: '#0D1B2A' },
  proteccion_civil: { icon: Shield,   color: '#D97706' },
  cruz_roja:        { icon: Hospital, color: '#DC2626' },
};

function SectionHeader({ icon: Icon, title, count, onAdd, addLabel, canEdit }) {
  return (
    <div className="flex items-center justify-between mb-3 mt-6">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={18} className="text-soem-primary" />}
        <h3 className="font-bold text-soem-navy">{title}</h3>
        {typeof count === 'number' && (
          <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{count}</span>
        )}
      </div>
      {onAdd && canEdit && (
        <button onClick={onAdd}
          className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5">
          <Plus size={12} /> {addLabel || 'Agregar'}
        </button>
      )}
    </div>
  );
}

function InheritedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">
      <Building2 size={9} /> Del venue
    </span>
  );
}

function EventExtraBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-full">
      ⭐ Solo este evento
    </span>
  );
}

// #ClickToPlace — Modal para preparar un map_point. NO pide lat/lng:
// el usuario completa categoría/nombre/notas, click "Marcar en el mapa"
// y luego clic en el mapa coloca el punto. Como OnePlan.
function MapPointModal({ point, onPickPosition, onSave, onClose, defaultCenter }) {
  const isEdit = !!point;
  const [form, setForm] = useState({
    category: point?.category || 'evacuation',
    name: point?.name || '',
    notes: point?.notes || '',
    lat: point?.lat?.toString() || '',
    lng: point?.lng?.toString() || '',
  });
  const [saving, setSaving] = useState(false);

  // #MapShapes — Detectar tipo (point/polygon/polyline) desde la categoría
  // seleccionada. Cambia el comportamiento del botón submit.
  const selectedType = (MAP_OBJECT_FLAT[form.category]?.type) || 'point';
  const isShape = selectedType === 'polygon' || selectedType === 'polyline';

  // En modo edición permitimos guardar directo (sin re-marcar). En modo
  // crear, el botón principal arma el placement y deja el resto al click.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEdit) {
      // Save edit con coords actuales (lat/lng del point)
      setSaving(true);
      try {
        await onSave({
          category: form.category,
          name: form.name || null,
          notes: form.notes || null,
          lat: point.lat,
          lng: point.lng,
        });
        onClose();
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Error al guardar';
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } finally { setSaving(false); }
    } else {
      // Crear: arma placement y deja al map click (punto o vértices del trazo)
      // (polygon/polyline) hacer el save final.
      onPickPosition({
        type: selectedType,
        category: form.category,
        name: form.name || null,
        notes: form.notes || null,
      });
      onClose();
    }
  };

  // #OnePlanTaxonomy — info de la categoría actual desde el flat lookup
  const currentMeta = MAP_OBJECT_FLAT[form.category] || MAP_POINT_CATEGORIES[form.category] || MAP_POINT_CATEGORIES.other;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy flex-shrink-0">
          <h3 className="font-bold text-white text-sm">{point ? 'Editar punto' : 'Nuevo punto en mapa'}</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar"><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="soem-label">Categoría *</label>
              {/* Pill mostrando categoría actual */}
              {form.category && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border"
                  style={{
                    backgroundColor: (currentMeta.color || '#94A3B8') + '20',
                    borderColor: (currentMeta.color || '#94A3B8') + '60',
                    color: currentMeta.color || '#475569',
                  }}>
                  {currentMeta.icon && <span>{currentMeta.icon}</span>}
                  <span className="font-semibold">{currentMeta.label || form.category}</span>
                </span>
              )}
            </div>
            {/* #OnePlanTaxonomy — Picker con árbol de ~200 objetos en español
                #MapShapes — Permite point, polygon y polyline (Fase C) */}
            <MapObjectPicker
              selectedId={form.category}
              onSelect={(item) => setForm(f => ({ ...f, category: item.id }))}
              allowedTypes={['point', 'polygon', 'polyline']}
              maxHeight={320}
            />
          </div>
          <div>
            <label className="soem-label block mb-1">Nombre (opcional)</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Salida sur, Punto Charlie"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary" />
          </div>
          <div>
            <label className="soem-label block mb-1">Notas</label>
            <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-soem-primary" />
          </div>
          {/* #ClickToPlace — En modo crear no pedimos lat/lng. Sólo en
              modo edición mostramos las coords actuales (read-only) */}
          {isEdit && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-500 font-mono">
              📍 Posición actual: {point.lat?.toFixed(6)}, {point.lng?.toFixed(6)}
              <br />
              <span className="text-slate-400 not-italic">Para mover el punto, arrástralo en el mapa.</span>
            </div>
          )}
          {!isEdit && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-700">
              {isShape && selectedType === 'polygon' && (
                <>💡 Al guardar, te llevará al mapa. Haz <strong>click</strong> en cada vértice del polígono y <strong>doble-click</strong> para cerrar el área.</>
              )}
              {isShape && selectedType === 'polyline' && (
                <>💡 Al guardar, te llevará al mapa. Haz <strong>click</strong> en cada punto de la ruta y <strong>doble-click</strong> para finalizar.</>
              )}
              {!isShape && (
                <>💡 Al guardar, te llevará al mapa para que hagas <strong>click</strong> en la ubicación exacta del punto.</>
              )}
            </div>
          )}
        </div>
        {/* Footer fijo con los botones */}
        <div className="border-t border-slate-200 p-4 flex gap-3 flex-shrink-0 bg-white">
          <button type="submit" disabled={saving || !form.category}
            className="soem-btn-primary flex-1 py-2 text-sm disabled:opacity-60">
            {saving
              ? 'Guardando...'
              : isEdit
                ? 'Guardar cambios'
                : isShape
                  ? (selectedType === 'polygon' ? 'Dibujar área en el mapa →' : 'Dibujar ruta en el mapa →')
                  : 'Marcar en el mapa →'}
          </button>
          <button type="button" onClick={onClose}
            className="soem-btn-secondary flex-1 py-2 text-sm">Cancelar</button>
        </div>
      </form>
    </div>
  );
}

// Modal para vincular emergency_service (lo levanta location mode)
function EmergencyLinkModal({ onLink, onClose, currentlyLinkedIds }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  useEffect(() => {
    emergencyServicesAPI.list({ limit: 500 })
      .then(r => setServices(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const filtered = services.filter(s =>
    !currentlyLinkedIds.includes(s.id) &&
    (!q || s.name?.toLowerCase().includes(q.toLowerCase()))
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
          <h3 className="font-bold text-white text-sm">Vincular servicio de emergencia</h3>
          <button onClick={onClose} aria-label="Cerrar"><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-4 border-b">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Buscar por nombre..."
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <p className="text-center text-slate-400 p-6 text-sm">Cargando...</p> :
           filtered.length === 0 ? <p className="text-center text-slate-400 p-6 text-sm">Sin resultados.</p> :
           filtered.map(s => {
             const ec = EMERGENCY_ICONS[s.institution_type] || { icon: AlertTriangle, color: '#64748B' };
             const Ico = ec.icon;
             return (
               <div key={s.id} onClick={() => { onLink(s); onClose(); }}
                 className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50/40 cursor-pointer border-b border-slate-100">
                 <Ico size={20} color={ec.color} />
                 <div className="flex-1 min-w-0">
                   <p className="text-sm font-semibold text-soem-navy truncate">{s.name}</p>
                   <p className="text-xs text-slate-400">{s.institution_type} {s.phone ? ` · ${s.phone}` : ''}</p>
                 </div>
                 <Plus size={14} className="text-soem-primary" />
               </div>
             );
           })}
        </div>
      </div>
    </div>
  );
}

// #UbicacionUnify — Modal para buscar servicios cercanos via Google Places
// y elegir cuáles persistir al venue. Funciona en modo location (POST a
// /locations/{id}/nearby-services/search → bulk al guardar).
function NearbyServicesSearchModal({ locationId, currentlySaved, onClose, onSaved, hasGps = true, getMapCenter }) {
  // Por default seleccionamos las 5 categorías más útiles. El usuario
  // puede des-tildar antes de buscar para acotar.
  const [categories, setCategories] = useState({
    hospital: true,
    gas_station: true,
    lodging: true,
    bank: false,
    restaurant: false,
  });
  const [radius, setRadius] = useState(10000); // metros
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null); // { hospital: [...], ... }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // IDs ya guardados para deshabilitar el checkbox y mostrar "Ya guardado"
  const savedPlaceIds = useMemo(
    () => new Set((currentlySaved || []).map((s) => s.place_id).filter(Boolean)),
    [currentlySaved]
  );

  const runSearch = async () => {
    setSearching(true);
    setError(null);
    setResults(null);
    setSelectedIds(new Set());
    try {
      const activeCats = Object.entries(categories).filter(([, on]) => on).map(([k]) => k);
      if (activeCats.length === 0) {
        toast.error('Selecciona al menos una categoría');
        setSearching(false);
        return;
      }
      // Sin GPS guardado, la búsqueda usa el centro actual del mapa.
      const mapCenter = !hasGps && getMapCenter ? getMapCenter() : null;
      const res = await locationsAPI.searchNearbyServices(locationId, {
        radius,
        categories: activeCats,
        ...(mapCenter ? { latitude: mapCenter.lat, longitude: mapCenter.lng } : {}),
      });
      const data = res.data?.results || {};
      // Sanitize: cada categoría puede traer array o {error}. Si error, omit.
      const cleanResults = {};
      for (const [cat, payload] of Object.entries(data)) {
        if (Array.isArray(payload)) {
          cleanResults[cat] = payload;
        } else if (payload && payload.error) {
          // Reporta el error de esa categoría sin romper las demás
          // eslint-disable-next-line no-console
          console.warn(`Search ${cat} failed:`, payload);
        }
      }
      setResults(cleanResults);
      const total = Object.values(cleanResults).reduce((acc, arr) => acc + arr.length, 0);
      if (total === 0) {
        toast.info('No se encontraron resultados con esos filtros');
      }
    } catch (err) {
      const raw = err?.response?.data?.detail || err?.message || 'Error desconocido';
      const detail = typeof raw === 'string' && raw.includes('gps_location')
        ? 'Esta ubicación no tiene coordenadas GPS. Edita la ubicación para fijar su punto en el mapa.'
        : (typeof raw === 'string' ? raw : 'Error desconocido');
      setError(detail);
      toast.error(`No se pudo buscar: ${detail}`);
    } finally {
      setSearching(false);
    }
  };

  const toggleSelected = (placeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(placeId)) next.delete(placeId);
      else next.add(placeId);
      return next;
    });
  };

  const handleSave = async () => {
    if (selectedIds.size === 0) {
      toast.error('Selecciona al menos un servicio');
      return;
    }
    setSaving(true);
    try {
      const flat = Object.values(results || {}).flat();
      const toSave = flat.filter((s) => selectedIds.has(s.place_id));
      const res = await locationsAPI.saveNearbyServicesBulk(locationId, toSave);
      const inserted = res.data?.inserted || 0;
      const skipped = res.data?.skipped || 0;
      if (inserted > 0 && skipped === 0) {
        toast.success(`${inserted} servicio${inserted === 1 ? '' : 's'} agregado${inserted === 1 ? '' : 's'} al venue`);
      } else if (inserted > 0 && skipped > 0) {
        toast.success(`${inserted} agregado(s), ${skipped} ya existía(n)`);
      } else if (skipped > 0) {
        toast.info(`Todos los ${skipped} servicios ya estaban guardados`);
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Error desconocido';
      toast.error(`No se pudo guardar: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const allResults = useMemo(() => {
    if (!results) return [];
    return Object.values(results).flat();
  }, [results]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
          <h3 className="font-bold text-white text-sm">Buscar servicios cercanos (Google Places)</h3>
          <button onClick={onClose} aria-label="Cerrar">
            <X size={18} className="text-slate-400 hover:text-white" />
          </button>
        </div>

        {/* Filtros */}
        <div className="p-4 border-b bg-slate-50 space-y-3">
          <div>
            <p className="soem-label mb-2">Categorías</p>
            <div className="flex flex-wrap gap-2">
              {NEARBY_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const checked = categories[cat.id];
                return (
                  <label key={cat.id}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border cursor-pointer text-xs font-semibold ${
                      checked
                        ? 'bg-blue-50 text-blue-700 border-blue-300'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                    }`}>
                    <input type="checkbox" className="hidden" checked={checked}
                      onChange={(e) => setCategories((c) => ({ ...c, [cat.id]: e.target.checked }))} />
                    <Icon size={13} color={checked ? cat.color : '#94A3B8'} />
                    {cat.label}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label htmlFor="radius" className="soem-label">Radio (metros)</label>
            <input id="radius" type="number" min="500" max="50000" step="500" value={radius}
              onChange={(e) => setRadius(parseInt(e.target.value) || 10000)}
              className="w-32 px-3 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary" />
            <span className="text-xs text-slate-400">≈ {(radius / 1000).toFixed(1)} km</span>
            <button onClick={runSearch} disabled={searching}
              className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5 ml-auto disabled:opacity-60">
              {searching ? <><RefreshCw size={12} className="animate-spin" /> Buscando…</> : <><Search size={12} /> Buscar</>}
            </button>
          </div>
          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}
        </div>

        {/* Resultados */}
        <div className="flex-1 overflow-y-auto p-4">
          {!results ? (
            <div className="text-center py-12 text-slate-400">
              <Search size={36} className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Configura las categorías y radio, luego click "Buscar".</p>
              <p className="text-xs mt-1">
                {hasGps
                  ? 'El centro de búsqueda es la ubicación GPS del venue.'
                  : 'Este venue no tiene coordenadas GPS: se buscará alrededor del centro actual del mapa. Para fijarlas, edita la ubicación.'}
              </p>
            </div>
          ) : allResults.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              Sin resultados. Prueba con otro radio o categorías.
            </div>
          ) : (
            Object.entries(results).map(([cat, items]) => {
              if (!items?.length) return null;
              const catCfg = NEARBY_CATEGORIES.find((c) => c.id === cat);
              const Icon = catCfg?.icon || MapPin;
              return (
                <div key={cat} className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} color={catCfg?.color || '#64748B'} />
                    <h4 className="font-bold text-soem-navy text-sm">{catCfg?.label || cat}</h4>
                    <span className="text-xs text-slate-400">({items.length})</span>
                  </div>
                  <div className="space-y-1">
                    {items.map((p) => {
                      const alreadySaved = savedPlaceIds.has(p.place_id);
                      const checked = selectedIds.has(p.place_id);
                      return (
                        <label key={p.place_id}
                          className={`flex items-start gap-3 px-3 py-2 rounded-sm border cursor-pointer ${
                            alreadySaved ? 'bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed' :
                            checked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}>
                          <input
                            type="checkbox"
                            checked={alreadySaved || checked}
                            disabled={alreadySaved}
                            onChange={() => !alreadySaved && toggleSelected(p.place_id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold text-soem-navy truncate">{p.name}</p>
                              {alreadySaved && (
                                <span className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
                                  Ya guardado
                                </span>
                              )}
                              {p.rating ? (
                                <span className="text-xs text-amber-600 flex items-center gap-0.5">
                                  <Star size={10} fill="#D97706" /> {p.rating}
                                </span>
                              ) : null}
                            </div>
                            <p className="text-xs text-slate-400 truncate mt-0.5">
                              {p.address || '—'}
                              {p.distance ? ` · ${(p.distance / 1000).toFixed(1)} km` : ''}
                            </p>
                            {p.phone && (
                              <p className="text-xs text-soem-primary flex items-center gap-1 mt-0.5">
                                <Phone size={9} /> {p.phone}
                              </p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t flex items-center justify-between bg-slate-50">
          <p className="text-xs text-slate-500">
            {selectedIds.size > 0
              ? `${selectedIds.size} seleccionado(s) para guardar`
              : 'Selecciona los servicios que quieres agregar'}
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="soem-btn-secondary px-3 py-1.5 text-xs">Cancelar</button>
            <button onClick={handleSave} disabled={saving || selectedIds.size === 0}
              className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-60">
              {saving ? 'Guardando…' : `Guardar ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Componente principal ──
export default function UbicacionTab({ mode, locationId, eventId, location }) {
  // mode: 'location' | 'event'
  const navigate = useNavigate();
  const { canDo, user } = useAuth();
  const role = user?.role || '';
  const canEditLocation = role === 'super_admin' || role === 'lider_proyecto';
  const canEditEventExtras = role === 'super_admin' || role === 'lider_proyecto';

  // Datos del backend
  // #MapShapes — incluye map_shapes (Fase C polygons + polylines)
  const [locData, setLocData] = useState({ map_points: [], map_shapes: [], nearby_services: [], emergency_services: [] });
  const [eventExtras, setEventExtras] = useState({ map_points: [], map_shapes: [], nearby_services: [], emergency_services: [] });
  const [resolvedLocation, setResolvedLocation] = useState(location || null);
  const [loading, setLoading] = useState(true);

  // Modales
  const [mapPointModal, setMapPointModal] = useState(null); // { scope: 'location'|'event', point?: {} }
  const [emergencyModal, setEmergencyModal] = useState(false);
  // #UbicacionUnify — modal de búsqueda Google Places para agregar
  // servicios cercanos al venue (solo modo location). Requiere
  // GOOGLE_MAPS_API_KEY configurada en el backend.
  const [nearbySearchModal, setNearbySearchModal] = useState(false);
  // #ClickToPlace — Modo "placement" como OnePlan: usuario arma el punto
  // en el modal (categoría/nombre/notas/scope), luego click en el mapa
  // captura lat/lng y guarda. Estado: null o { scope, category, name, notes }.
  const [placingPoint, setPlacingPoint] = useState(null);
  // #MapShapes — vértices del área o ruta que se está trazando.
  const [draftPath, setDraftPathState] = useState([]);
  // Espejo en ref: el doble click llega antes de que React vuelva a renderizar.
  const draftPathRef = useRef([]);
  const setDraftPath = (next) => {
    const value = typeof next === 'function' ? next(draftPathRef.current) : next;
    draftPathRef.current = value;
    setDraftPathState(value);
  };
  // Al entrar o salir del modo colocación se descarta cualquier trazo previo.
  useEffect(() => { setDraftPath([]); }, [placingPoint]); // eslint-disable-line react-hooks/exhaustive-deps
  // #ClickToPlace — Marker seleccionado para mostrar InfoWindow (nombre,
  // categoría, notas, botones Editar/Eliminar)
  const [selectedMarker, setSelectedMarker] = useState(null);
  // #ClickToPlace — Saving state mientras corremos POST tras el click
  const [placingSave, setPlacingSave] = useState(false);
  // #NearbyMarkers — Highlight cross-link entre la lista y el mapa:
  // null | { type: 'nearby'|'emergency', id }. Al hover/click en un row
  // de la lista, este state se setea y el marker correspondiente cambia
  // de color + scale. Inverso: click en marker -> abre InfoWindow.
  const [highlightedService, setHighlightedService] = useState(null);
  // #NearbyMarkers — InfoWindow del marker de servicio cercano/emergencia
  const [selectedService, setSelectedService] = useState(null); // { svc, type }
  // #MapShapes — Shape (polygon/polyline) seleccionado para InfoWindow
  const [selectedShape, setSelectedShape] = useState(null);

  // #UbicacionFullscreen — modo vista pantalla completa, ESC para salir.
  // Mientras está activo el body se bloquea el scroll y el wrapper toma
  // viewport entero con z-50. El mapa crece a calc(100vh - 140px) para
  // trabajar la locación cómodo, como hace OnePlan en su editor.
  const [isFullscreen, setIsFullscreen] = useState(false);
  // #MapReport (Fase D) — exporta PDF profesional del mapa
  const [exportingPdf, setExportingPdf] = useState(false);
  // #MapIcons (Fase E) — Sidebar fijo izquierdo con el árbol OnePlan.
  // Click en un item arranca placement mode (no requiere modal).
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // #MapIcons — Ref a la instancia de Google Map para preservar viewport
  // (center/zoom) cuando re-renderizamos por reload(). Se setea en onLoad.
  const mapRef = useRef(null);
  // Initial center calculado UNA SOLA VEZ (no se re-aplica al re-render);
  // luego el usuario puede pan/zoom libremente.
  const initialCenterRef = useRef(null);
  const initialZoomRef = useRef(16);

  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullscreen]);

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const reload = async () => {
    setLoading(true);
    try {
      if (mode === 'event') {
        const r = await eventsAPI.getMapAndServices(eventId);
        const d = r.data || {};
        setLocData(d.location_data || { map_points: [], map_shapes: [], nearby_services: [], emergency_services: [] });
        setEventExtras(d.event_extras || { map_points: [], map_shapes: [], nearby_services: [], emergency_services: [] });
        if (d.location) setResolvedLocation(d.location);
      } else {
        // #MapShapes — fetch shapes en paralelo con map_points
        const [mpRes, msRes, nsRes, esRes] = await Promise.all([
          locationsAPI.getMapPoints(locationId).catch(() => ({ data: { items: [] } })),
          locationsAPI.getMapShapes(locationId).catch(() => ({ data: { items: [] } })),
          locationsAPI.getNearbyServices(locationId).catch(() => ({ data: { items: [] } })),
          emergencyServicesAPI.list({ location_id: locationId, limit: 200 }).catch(() => ({ data: { items: [] } })),
        ]);
        setLocData({
          map_points: mpRes.data?.items || [],
          map_shapes: msRes.data?.items || [],
          nearby_services: nsRes.data?.items || [],
          emergency_services: esRes.data?.items || [],
        });
      }
    } catch (err) {
      console.error('UbicacionTab reload failed', err);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, locationId, eventId]);

  // Coordenadas centro del mapa — útil para fallback al inicio.
  const center = useMemo(() => {
    const gps = resolvedLocation?.gps_location || resolvedLocation?.coordinates;
    if (gps) {
      const lat = gps.lat ?? gps[1];
      const lng = gps.lng ?? gps[0];
      if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng };
    }
    return { lat: 19.4326, lng: -99.1332 };
  }, [resolvedLocation]);
  // El centro por defecto (CDMX) no es la ubicación del venue: sirve para
  // avisar en la búsqueda de servicios cercanos cuando falta el GPS.
  const hasVenueGps = useMemo(() => {
    const gps = resolvedLocation?.gps_location || resolvedLocation?.coordinates;
    const lat = gps?.lat ?? gps?.[1];
    const lng = gps?.lng ?? gps?.[0];
    return typeof lat === 'number' && typeof lng === 'number';
  }, [resolvedLocation]);

  // #MapIcons — calcular initialCenter UNA vez para defaultCenter de GoogleMap.
  // Después de eso, el usuario controla pan/zoom y reload() NO mueve el viewport.
  const viewportInitializedRef = useRef(false);
  if (initialCenterRef.current == null && resolvedLocation) {
    initialCenterRef.current = center;
  }
  // Si el mapa cargó antes que el resolvedLocation, set center cuando llegue.
  useEffect(() => {
    if (viewportInitializedRef.current) return;
    if (mapRef.current && initialCenterRef.current) {
      mapRef.current.setCenter(initialCenterRef.current);
      mapRef.current.setZoom(initialZoomRef.current);
      viewportInitializedRef.current = true;
    }
  }, [resolvedLocation]);

  // Map markers consolidados (location_points en blue, event_points en purple)
  const markers = useMemo(() => {
    const out = [];
    (locData.map_points || []).forEach(p => out.push({ ...p, _scope: 'location' }));
    if (mode === 'event') {
      (eventExtras.map_points || []).forEach(p => out.push({ ...p, _scope: 'event' }));
    }
    return out;
  }, [locData.map_points, eventExtras.map_points, mode]);

  // #MapShapes — Shapes consolidados (polygons + polylines). Igual que markers
  // mantiene el _scope para mostrar quién es del venue vs solo-este-evento.
  const shapes = useMemo(() => {
    const out = [];
    (locData.map_shapes || []).forEach(s => out.push({ ...s, _scope: 'location' }));
    if (mode === 'event') {
      (eventExtras.map_shapes || []).forEach(s => out.push({ ...s, _scope: 'event' }));
    }
    return out;
  }, [locData.map_shapes, eventExtras.map_shapes, mode]);

  // ── Actions ──
  const handleCreateMapPoint = async (data) => {
    const scope = mapPointModal?.scope || 'location';
    if (scope === 'event') {
      await eventsAPI.createMapPoint(eventId, data);
    } else {
      await locationsAPI.createMapPoint(locationId, data);
    }
    toast.success('Punto agregado');
    await reload();
  };

  // #ClickToPlace — Cuando el modal "Marcar en el mapa" se cierra,
  // recibimos {category, name, notes, scope} pero falta lat/lng. Lo
  // guardamos en placingPoint y esperamos al map click.
  const startPlacement = (placement) => {
    const scope = mapPointModal?.scope || 'location';
    setPlacingPoint({ ...placement, scope });
  };

  // #MapIcons — Click directo en un item del sidebar arranca placement mode.
  // No hay modal: el nombre/notas se pueden editar luego haciendo click en
  // el marker. UX igual a OnePlan.
  const startPlacementFromSidebar = (item) => {
    if (!canEditLocation && mode === 'location') {
      toast.error('No tienes permisos para editar este venue');
      return;
    }
    if (mode === 'event' && !canEditEventExtras) {
      toast.error('No tienes permisos para editar el evento');
      return;
    }
    const itemType = item.type || 'point';
    const scope = mode === 'event' ? 'event' : 'location';
    setPlacingPoint({
      type: itemType,
      category: item.id,
      name: null,
      notes: null,
      scope,
    });
  };

  // #ClickToPlace — Handler del click sobre el mapa cuando estamos en
  // placement mode. Saca lat/lng del evento de Google Maps, hace POST y
  // limpia el state.
  const handleMapClick = async (e) => {
    if (!placingPoint || placingSave) return;
    const lat = e?.latLng?.lat?.();
    const lng = e?.latLng?.lng?.();
    if (lat == null || lng == null) return;
    if (placingPoint.type === 'polygon' || placingPoint.type === 'polyline') {
      setDraftPath((prev) => [...prev, { lat, lng }]);
      return;
    }
    setPlacingSave(true);
    const payload = {
      category: placingPoint.category,
      name: placingPoint.name,
      notes: placingPoint.notes,
      lat,
      lng,
    };
    try {
      if (placingPoint.scope === 'event') {
        await eventsAPI.createMapPoint(eventId, payload);
      } else {
        await locationsAPI.createMapPoint(locationId, payload);
      }
      toast.success('Punto agregado en el mapa');
      setPlacingPoint(null);
      await reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Error al guardar';
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar punto');
    } finally {
      setPlacingSave(false);
    }
  };

  // #ClickToPlace — Drag-to-reposition. Al soltar un marker en otra
  // posición, hacemos PATCH del punto con las nuevas coords.
  const handleMarkerDragEnd = async (point, e) => {
    const lat = e?.latLng?.lat?.();
    const lng = e?.latLng?.lng?.();
    if (lat == null || lng == null) return;
    try {
      if (point._scope === 'event' || point.event_id) {
        await eventsAPI.updateMapPoint(eventId, point.id, { lat, lng });
      } else {
        await locationsAPI.updateMapPoint(locationId || resolvedLocation?.id, point.id, { lat, lng });
      }
      toast.success('Posición actualizada');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al mover el punto');
      reload(); // re-fetch para revertir visualmente si falló
    }
  };

  const handleDeleteMapPoint = async (point) => {
    if (!window.confirm('¿Eliminar este punto?')) return;
    try {
      if (point._scope === 'event' || point.event_id) {
        await eventsAPI.deleteMapPoint(eventId, point.id);
      } else {
        await locationsAPI.deleteMapPoint(locationId || resolvedLocation?.id, point.id);
      }
      toast.success('Eliminado');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar');
    }
  };

  // ── #MapShapes — Handlers para shapes (polygon + polyline) ──

  // Extrae lat/lng del MVCArray de Google Maps. Usado al terminar dibujo
  // y al editar vértices con el handle nativo de Google.
  const extractCoords = (mvcPath) => {
    const out = [];
    if (!mvcPath || typeof mvcPath.forEach !== 'function') return out;
    mvcPath.forEach((latLng) => {
      out.push({ lat: latLng.lat(), lng: latLng.lng() });
    });
    return out;
  };

  // #MapShapes — Google retiró DrawingManager en Maps JS 3.65 (al crearlo
  // lanza un error y tumba la página). El trazado ahora es propio: cada click
  // agrega un vértice a draftPath; "Terminar" (o doble click) guarda.
  const isShapeMode = !!placingPoint && (placingPoint.type === 'polygon' || placingPoint.type === 'polyline');

  const saveShape = async (type, rawCoords) => {
    if (!placingPoint || placingPoint.type !== type || placingSave) return;
    const coords = dedupePath(rawCoords);
    if (!canFinishShape(type, coords)) {
      toast.error(type === 'polygon' ? 'Un área requiere al menos 3 vértices' : 'Una ruta requiere al menos 2 puntos');
      return;
    }
    setPlacingSave(true);
    try {
      const data = {
        type,
        category: placingPoint.category,
        name: placingPoint.name,
        notes: placingPoint.notes,
        coordinates: coords,
      };
      if (placingPoint.scope === 'event') {
        await eventsAPI.createMapShape(eventId, data);
      } else {
        await locationsAPI.createMapShape(locationId || resolvedLocation?.id, data);
      }
      toast.success(type === 'polygon' ? 'Área guardada' : 'Ruta guardada');
      setPlacingPoint(null);
      setDraftPath([]);
      await reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || (type === 'polygon' ? 'Error al guardar el área' : 'Error al guardar la ruta');
      toast.error(typeof msg === 'string' ? msg : 'No se pudo guardar el trazo');
    } finally {
      setPlacingSave(false);
    }
  };

  const finishShape = () => saveShape(placingPoint?.type, draftPathRef.current);
  const undoShapePoint = () => setDraftPath((prev) => prev.slice(0, -1));

  // #MapShapes — Al terminar drag (mover shape completo) o editar un
  // vértice (editable=true permite arrastrar handles), persiste las nuevas
  // coordenadas. Backend recomputa metrics automáticamente.
  const persistShapeCoords = async (shape, newCoords) => {
    try {
      if (shape._scope === 'event' || shape.event_id) {
        await eventsAPI.updateMapShape(eventId, shape.id, { coordinates: newCoords });
      } else {
        await locationsAPI.updateMapShape(locationId || resolvedLocation?.id, shape.id, { coordinates: newCoords });
      }
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar shape');
      reload();
    }
  };

  // #MapReport (Fase D) — descarga el PDF generado por el backend. Necesita
  // responseType:'blob' + crear un object URL temporal porque el endpoint
  // requiere Bearer token (no podemos usar <a href> directamente).
  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const res = mode === 'event'
        ? await eventsAPI.exportMapReportPdf(eventId)
        : await locationsAPI.exportMapReportPdf(locationId || resolvedLocation?.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      // Abre en nueva pestaña — el browser muestra el PDF inline y permite
      // descargar/imprimir desde su propia UI.
      const win = window.open(url, '_blank');
      if (!win) {
        // Popup blocked → force download
        const a = document.createElement('a');
        a.href = url;
        a.download = mode === 'event' ? `mapa-evento.pdf` : `mapa-${resolvedLocation?.name || 'venue'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      // Limpia el blob URL después de un rato
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success('Reporte PDF generado');
    } catch (err) {
      // El body de error podría ser un Blob (JSON envuelto), tenemos que leerlo
      let msg = 'Error al generar PDF';
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const obj = JSON.parse(text);
          msg = obj?.detail || msg;
        } catch (_) {}
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(typeof msg === 'string' ? msg : 'Error al generar PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleDeleteShape = async (shape) => {
    if (!window.confirm(`¿Eliminar ${shape.type === 'polygon' ? 'este polígono' : 'esta línea'}?`)) return;
    try {
      if (shape._scope === 'event' || shape.event_id) {
        await eventsAPI.deleteMapShape(eventId, shape.id);
      } else {
        await locationsAPI.deleteMapShape(locationId || resolvedLocation?.id, shape.id);
      }
      toast.success('Eliminado');
      setSelectedShape(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleDeleteNearby = async (svc, scope) => {
    if (!window.confirm('¿Eliminar este servicio cercano?')) return;
    try {
      if (scope === 'event') {
        await eventsAPI.deleteNearbyService(eventId, svc.id);
      } else {
        await locationsAPI.deleteNearbyService(locationId || resolvedLocation?.id, svc.id);
      }
      toast.success('Eliminado');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleLinkEmergency = async (svc) => {
    try {
      if (mode === 'event') {
        // En modo event vinculamos al evento; admin debería vincular al venue
        // desde la página de locación.
        toast.info('Vincula el servicio desde la página del venue para que se herede a todos los eventos');
        return;
      }
      await locationsAPI.linkEmergencyService(locationId, svc.id);
      toast.success(`Vinculado ${svc.name}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al vincular');
    }
  };

  const handleUnlinkEmergency = async (svc, scope) => {
    if (!window.confirm(`¿Desvincular ${svc.name}?`)) return;
    try {
      if (scope === 'event') {
        toast.info('Para desvincular del venue, edita la locación');
        return;
      }
      await locationsAPI.unlinkEmergencyService(locationId, svc.id);
      toast.success('Desvinculado');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al desvincular');
    }
  };

  const goToVenue = () => {
    if (resolvedLocation?.id) navigate(`/dashboard/locations/${resolvedLocation.id}`);
  };

  // ── Render ──
  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;
  }

  // #UbicacionFullscreen — wrapper classes condicionales
  const wrapperCls = isFullscreen
    ? 'fixed inset-0 z-50 bg-white overflow-auto p-4 space-y-2'
    : 'space-y-2';
  const mapHeight = isFullscreen ? 'calc(100vh - 140px)' : 380;

  return (
    <div className={wrapperCls}>
      {/* #UbicacionFullscreen — Toolbar fullscreen con título + cerrar */}
      {isFullscreen && (
        <div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-2">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-soem-primary" />
            <h2 className="font-bold text-soem-navy text-base">
              Editor de Ubicación{resolvedLocation?.name ? ` — ${resolvedLocation.name}` : ''}
            </h2>
            <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-sm">ESC para salir</span>
          </div>
          <div className="flex items-center gap-2">
            {/* #MapReport — Exportar PDF profesional */}
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exportingPdf}
              title="Exportar mapa como PDF profesional (igual que OnePlan)"
              className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-60">
              <FileDown size={12} />
              {exportingPdf ? 'Generando...' : 'Exportar PDF'}
            </button>
            <button
              type="button"
              onClick={() => setIsFullscreen(false)}
              className="soem-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5">
              <Minimize2 size={12} /> Salir de pantalla completa
            </button>
          </div>
        </div>
      )}

      {/* Banner contextual en modo event */}
      {mode === 'event' && resolvedLocation && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <Building2 size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-800 min-w-0">
              <p className="font-semibold truncate">Datos heredados del venue: {resolvedLocation.name}</p>
              <p className="text-blue-600 mt-0.5">
                Lo del venue es read-only aquí. Edítalo desde la página de la locación.
                Lo de "Solo este evento" se gestiona aquí.
              </p>
            </div>
          </div>
          {canEditLocation && (
            <button onClick={goToVenue}
              className="soem-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 flex-shrink-0">
              Editar venue <ExternalLink size={11} />
            </button>
          )}
        </div>
      )}

      {/* #ClickToPlace — Banner activo en placement mode (point/polygon/polyline) */}
      {placingPoint && (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-sm px-4 py-2.5 flex items-center justify-between gap-3 mb-2 animate-pulse">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <span style={{ fontSize: 18 }}>{getCategoryMeta(placingPoint.category).icon}</span>
            <span>
              <strong>Modo colocación activo —</strong>{' '}
              {placingPoint.type === 'polygon'
                ? `Haz click en cada vértice del área y luego "Terminar" (o doble click). Vértices: ${draftPath.length}.`
                : placingPoint.type === 'polyline'
                  ? `Haz click en cada punto de la ruta y luego "Terminar" (o doble click). Puntos: ${draftPath.length}.`
                  : 'Haz click en el mapa para colocar:'}&nbsp;
              <strong>{getCategoryMeta(placingPoint.category).label}</strong>
              {placingPoint.name ? ` "${placingPoint.name}"` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isShapeMode && (
              <>
                <button
                  type="button"
                  onClick={undoShapePoint}
                  disabled={placingSave || draftPath.length === 0}
                  className="soem-btn-secondary px-3 py-1 text-xs disabled:opacity-60">
                  Deshacer punto
                </button>
                <button
                  type="button"
                  onClick={finishShape}
                  disabled={placingSave || !canFinishShape(placingPoint.type, draftPath)}
                  className="soem-btn-primary px-3 py-1 text-xs disabled:opacity-60">
                  Terminar
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setPlacingPoint(null)}
              disabled={placingSave}
              className="soem-btn-secondary px-3 py-1 text-xs disabled:opacity-60">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* #MapIcons (Fase E) — Layout flex con sidebar OnePlan-style a la
          izquierda + mapa a la derecha. Sidebar es colapsable. */}
      {mapsLoaded ? (
        <div className="flex gap-2" style={{ height: mapHeight }}>
          {/* Sidebar fijo izquierdo con árbol de objetos OnePlan */}
          {sidebarOpen && (canEditLocation || mode === 'event') && (
            <div className="w-64 flex-shrink-0 flex flex-col bg-white border border-slate-200 rounded-sm">
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-1.5">
                  <Plus size={13} className="text-soem-primary" />
                  <span className="text-xs font-bold text-soem-navy">Objetos del mapa</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  title="Ocultar sidebar"
                  className="text-slate-400 hover:text-slate-600">
                  <X size={13} />
                </button>
              </div>
              <p className="px-3 py-1.5 text-[10px] text-slate-500 border-b border-slate-100 bg-amber-50">
                💡 Click en un objeto, luego click en el mapa para colocarlo
              </p>
              <div className="flex-1 min-h-0 p-1">
                <MapObjectPicker
                  selectedId={placingPoint?.category || null}
                  onSelect={startPlacementFromSidebar}
                  allowedTypes={['point', 'polygon', 'polyline']}
                  maxHeight="100%"
                />
              </div>
            </div>
          )}
          {!sidebarOpen && (canEditLocation || mode === 'event') && (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              title="Mostrar sidebar de objetos"
              className="self-start mt-2 ml-0 px-2 py-1.5 bg-white shadow-md border border-slate-200 rounded-sm hover:bg-slate-50 flex items-center gap-1 text-xs">
              <Plus size={12} /> Objetos
            </button>
          )}
          {/* Map container */}
        <div className="rounded-sm border border-slate-200 overflow-hidden relative flex-1" style={{ height: '100%' }}>
          {/* #UbicacionFullscreen — botón flotante toggle fullscreen */}
          <button
            type="button"
            onClick={() => setIsFullscreen((v) => !v)}
            title={isFullscreen ? 'Salir de pantalla completa (ESC)' : 'Pantalla completa'}
            className="absolute top-2 right-2 z-20 bg-white shadow-md border border-slate-200 rounded-sm p-1.5 hover:bg-slate-50 transition-colors"
            style={{ lineHeight: 0 }}>
            {isFullscreen
              ? <Minimize2 size={14} className="text-slate-700" />
              : <Maximize2 size={14} className="text-slate-700" />}
          </button>
          {/* #MapReport (Fase D) — botón flotante export PDF */}
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exportingPdf}
            title="Exportar mapa como PDF profesional"
            className="absolute top-2 right-12 z-20 bg-white shadow-md border border-slate-200 rounded-sm p-1.5 hover:bg-slate-50 transition-colors disabled:opacity-60"
            style={{ lineHeight: 0 }}>
            <FileDown size={14} className="text-slate-700" />
          </button>
          {placingSave && (
            <div className="absolute inset-0 z-10 bg-black/30 flex items-center justify-center">
              <div className="bg-white rounded-sm px-4 py-2 text-sm text-slate-700 shadow-lg">Guardando punto...</div>
            </div>
          )}
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            /* #MapIcons — Omitimos center/zoom para que el mapa sea uncontrolled:
               setCenter inicial via onLoad y reload() NO mueve el viewport.
               El usuario controla pan/zoom libremente. */
            onLoad={(map) => {
              mapRef.current = map;
              const initial = initialCenterRef.current || center;
              if (initial) {
                map.setCenter(initial);
                map.setZoom(initialZoomRef.current);
              }
            }}
            onClick={placingPoint ? handleMapClick : undefined}
            onDblClick={isShapeMode ? () => finishShape() : undefined}
            options={{
              draggableCursor: placingPoint ? 'crosshair' : undefined,
              clickableIcons: !placingPoint,  // disable POI clicks en place mode
              disableDoubleClickZoom: isShapeMode,  // el doble click termina el trazo
            }}
          >
            {/* #MapShapes — Vista previa del trazo en curso (reemplaza a
                DrawingManager que Google retiró en Maps JS 3.65). */}
            {isShapeMode && draftPath.length > 0 && (
              placingPoint.type === 'polygon' && draftPath.length >= 3 ? (
                <Polygon
                  path={draftPath}
                  options={{
                    fillColor: getCategoryMeta(placingPoint.category).color || '#1A56DB',
                    fillOpacity: 0.25,
                    strokeColor: getCategoryMeta(placingPoint.category).color || '#1A56DB',
                    strokeWeight: 2,
                    clickable: false,
                    zIndex: 10,
                  }}
                />
              ) : (
                <Polyline
                  path={draftPath}
                  options={{
                    strokeColor: getCategoryMeta(placingPoint.category).color || '#0EA5E9',
                    strokeWeight: 3,
                    clickable: false,
                    zIndex: 10,
                  }}
                />
              )
            )}
            {isShapeMode && draftPath.map((pt, i) => (
              <Marker
                key={`draft-${i}`}
                position={pt}
                clickable={false}
                icon={window.google?.maps ? {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 5,
                  fillColor: '#ffffff',
                  fillOpacity: 1,
                  strokeColor: getCategoryMeta(placingPoint.category).color || '#1A56DB',
                  strokeWeight: 2,
                } : undefined}
              />
            ))}

            {/* #MapShapes — Render de polygons existentes con editable nativo
                de Google (vertex drag). Drag de shape completo via draggable. */}
            {shapes.filter(s => s.type === 'polygon').map((s) => {
              const cfg = getCategoryMeta(s.category);
              const st = s.style || {};
              const canEditShape =
                placingPoint == null && (
                  (s._scope === 'event' && canEditEventExtras) ||
                  (s._scope !== 'event' && canEditLocation && mode === 'location')
                );
              return (
                <Polygon
                  key={`shape-poly-${s._scope}-${s.id}`}
                  paths={s.coordinates}
                  editable={canEditShape}
                  draggable={canEditShape}
                  onClick={() => !placingPoint && setSelectedShape(s)}
                  onMouseUp={canEditShape ? (e) => {
                    // Cuando se suelta tras editar vértice o drag-whole-shape,
                    // los handlers nativos de Google ya actualizaron el path.
                    const poly = e?.domEvent?.target?.__polygonRef;
                    // Workaround: react-google-maps no expone el polygon directamente
                    // Lo recuperamos del overlay activo via path lookup.
                  } : undefined}
                  onLoad={(polygon) => {
                    // Suscribir a cambios del path para persistir
                    if (!canEditShape) return;
                    const path = polygon.getPath();
                    const onChange = () => {
                      const next = extractCoords(path);
                      if (next.length >= 3) persistShapeCoords(s, next);
                    };
                    // Google Maps emite set_at/insert_at/remove_at en MVCArray
                    if (window.google?.maps?.event) {
                      window.google.maps.event.addListener(path, 'set_at', onChange);
                      window.google.maps.event.addListener(path, 'insert_at', onChange);
                      window.google.maps.event.addListener(path, 'remove_at', onChange);
                    }
                  }}
                  options={{
                    fillColor: st.fill_color || cfg.color || SHAPE_DEFAULTS.polygon.fill_color,
                    fillOpacity: st.fill_opacity ?? SHAPE_DEFAULTS.polygon.fill_opacity,
                    strokeColor: st.stroke_color || cfg.color || SHAPE_DEFAULTS.polygon.stroke_color,
                    strokeWeight: st.stroke_width || SHAPE_DEFAULTS.polygon.stroke_width,
                    zIndex: s._scope === 'event' ? 3 : 2,
                  }}
                />
              );
            })}

            {/* #MapShapes — Render de polylines existentes (rutas, accesos) */}
            {shapes.filter(s => s.type === 'polyline').map((s) => {
              const cfg = getCategoryMeta(s.category);
              const st = s.style || {};
              const canEditShape =
                placingPoint == null && (
                  (s._scope === 'event' && canEditEventExtras) ||
                  (s._scope !== 'event' && canEditLocation && mode === 'location')
                );
              return (
                <Polyline
                  key={`shape-line-${s._scope}-${s.id}`}
                  path={s.coordinates}
                  editable={canEditShape}
                  draggable={canEditShape}
                  onClick={() => !placingPoint && setSelectedShape(s)}
                  onLoad={(polyline) => {
                    if (!canEditShape) return;
                    const path = polyline.getPath();
                    const onChange = () => {
                      const next = extractCoords(path);
                      if (next.length >= 2) persistShapeCoords(s, next);
                    };
                    if (window.google?.maps?.event) {
                      window.google.maps.event.addListener(path, 'set_at', onChange);
                      window.google.maps.event.addListener(path, 'insert_at', onChange);
                      window.google.maps.event.addListener(path, 'remove_at', onChange);
                    }
                  }}
                  options={{
                    strokeColor: st.stroke_color || cfg.color || SHAPE_DEFAULTS.polyline.stroke_color,
                    strokeWeight: st.stroke_width || SHAPE_DEFAULTS.polyline.stroke_width,
                    zIndex: s._scope === 'event' ? 3 : 2,
                  }}
                />
              );
            })}

            {/* #MapShapes — InfoWindow al click sobre un shape: muestra nombre,
                categoría, notas y métrica (área/perímetro o longitud) */}
            {selectedShape && (selectedShape.coordinates?.length ?? 0) > 0 && (() => {
              const cfg = getCategoryMeta(selectedShape.category);
              const m = selectedShape.metrics || {};
              // Centroide aproximado para posicionar el InfoWindow
              const pts = selectedShape.coordinates;
              const lat = pts.reduce((a, p) => a + p.lat, 0) / pts.length;
              const lng = pts.reduce((a, p) => a + p.lng, 0) / pts.length;
              const canEditShape =
                (selectedShape._scope === 'event' && canEditEventExtras) ||
                (selectedShape._scope !== 'event' && canEditLocation && mode === 'location');
              return (
                <InfoWindow position={{ lat, lng }} onCloseClick={() => setSelectedShape(null)}>
                  <div className="min-w-[200px] max-w-[280px]">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: cfg.color || '#94A3B8' }}
                      />
                      {cfg.icon && <span style={{ fontSize: 14 }}>{cfg.icon}</span>}
                      <p className="font-bold text-soem-navy text-sm">
                        {selectedShape.name || cfg.label}
                      </p>
                    </div>
                    {selectedShape.name && (
                      <p className="text-[10px] text-slate-500 mb-1">{cfg.label}</p>
                    )}
                    <p className="text-[11px] text-slate-600 mb-1">
                      {selectedShape.type === 'polygon' ? 'Polígono' : 'Ruta / Línea'}
                      {' · '}
                      {selectedShape._scope === 'event' ? '⭐ Solo este evento' : '📍 Venue'}
                    </p>
                    {selectedShape.type === 'polygon' && (
                      <div className="text-[11px] text-slate-700 bg-slate-50 rounded-sm p-1.5 mb-2 font-mono">
                        Área: <strong>{fmtArea(m.area_m2)}</strong>
                        <br />
                        Perímetro: <strong>{fmtMeters(m.perimeter_m)}</strong>
                      </div>
                    )}
                    {selectedShape.type === 'polyline' && (
                      <div className="text-[11px] text-slate-700 bg-slate-50 rounded-sm p-1.5 mb-2 font-mono">
                        Longitud: <strong>{fmtMeters(m.length_m)}</strong>
                      </div>
                    )}
                    {selectedShape.notes && (
                      <p className="text-[11px] text-slate-500 italic mb-2">{selectedShape.notes}</p>
                    )}
                    {canEditShape && (
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleDeleteShape(selectedShape)}
                          className="text-xs px-2 py-1 bg-red-50 text-red-700 hover:bg-red-100 rounded-sm flex items-center gap-1">
                          <Trash2 size={11} /> Eliminar
                        </button>
                      </div>
                    )}
                    {selectedShape.coordinates.length > 0 && (
                      <p className="text-[9px] text-slate-400 mt-1">
                        {selectedShape.coordinates.length} vértices · arrastra para editar
                      </p>
                    )}
                  </div>
                </InfoWindow>
              );
            })()}

            {markers.map(p => {
              const cfg = getCategoryMeta(p.category);
              const isInherited = p._scope !== 'event';
              const canDrag =
                placingPoint == null && (
                  (p._scope === 'event' && canEditEventExtras) ||
                  (p._scope !== 'event' && canEditLocation && mode === 'location')
                );
              return (
                <Marker
                  key={`${p._scope}-${p.id}`}
                  position={{ lat: p.lat, lng: p.lng }}
                  title={`[${isInherited ? '📍 Venue' : '⭐ Evento'}] ${cfg.label}: ${p.name || ''}`}
                  draggable={canDrag}
                  onDragEnd={canDrag ? (e) => handleMarkerDragEnd(p, e) : undefined}
                  onClick={() => !placingPoint && setSelectedMarker(p)}
                  /* #MapIcons — pictograma circular con color de categoría
                     y silueta blanca dentro. Anchor en la base del pin. */
                  icon={window.google?.maps ? {
                    url: buildMarkerIconUrl({
                      categoryId: p.category,
                      color: cfg.color || '#1A56DB',
                      size: 40,
                      asPin: true,
                    }),
                    scaledSize: new window.google.maps.Size(40, 50),
                    anchor: new window.google.maps.Point(20, 50),
                  } : undefined}
                />
              );
            })}

            {/* #NearbyMarkers — Markers de servicios cercanos (Google Places)
                Color por categoría. Si el row está highlighted (hover en la
                lista) el marker crece + cambia a amarillo brillante. */}
            {(locData.nearby_services || []).map((svc) => {
              const lat = svc.latitude ?? svc.lat;
              const lng = svc.longitude ?? svc.lng;
              if (lat == null || lng == null) return null;
              const catKey = svc.category || svc.type || 'other';
              const catCfg = NEARBY_CATEGORIES.find((c) => c.id === catKey);
              const color = catCfg?.color || '#64748B';
              const isHighlighted =
                highlightedService?.type === 'nearby' && highlightedService?.id === svc.id;
              return (
                <Marker
                  key={`ns-${svc.id}`}
                  position={{ lat, lng }}
                  title={svc.name || ''}
                  onClick={() => setSelectedService({ svc, type: 'nearby' })}
                  icon={{
                    // Círculo simple — distinto del shape de map_points para
                    // que el usuario diferencie visualmente categorías.
                    path: 0, // google.maps.SymbolPath.CIRCLE
                    fillColor: isHighlighted ? '#FACC15' : color,
                    fillOpacity: isHighlighted ? 1 : 0.85,
                    strokeColor: isHighlighted ? '#0D1B2A' : '#FFFFFF',
                    strokeWeight: isHighlighted ? 3 : 2,
                    scale: isHighlighted ? 13 : 9,
                  }}
                  zIndex={isHighlighted ? 999 : 1}
                />
              );
            })}

            {/* #NearbyMarkers — Markers de servicios de emergencia
                vinculados (hospital/bomberos/policía/etc). Cuadrados para
                diferenciarlos de los Google Places. */}
            {(locData.emergency_services || []).map((svc) => {
              const lat = svc.latitude ?? svc.lat ?? svc.gps_location?.lat;
              const lng = svc.longitude ?? svc.lng ?? svc.gps_location?.lng;
              if (lat == null || lng == null) return null;
              const inst = String(svc.institution_type || '').toLowerCase();
              const EMG_COLORS = {
                hospital: '#1A56DB',
                bomberos: '#DC2626',
                policia: '#0D1B2A',
                proteccion_civil: '#D97706',
                cruz_roja: '#DC2626',
              };
              const color = EMG_COLORS[inst] || '#DC2626';
              const isHighlighted =
                highlightedService?.type === 'emergency' && highlightedService?.id === svc.id;
              return (
                <Marker
                  key={`em-${svc.id}`}
                  position={{ lat, lng }}
                  title={`${svc.name || ''} — ${inst}`}
                  onClick={() => setSelectedService({ svc, type: 'emergency' })}
                  icon={{
                    // Marker en forma de cruz para distinguir emergencia
                    path: 'M -3 -10 L 3 -10 L 3 -3 L 10 -3 L 10 3 L 3 3 L 3 10 L -3 10 L -3 3 L -10 3 L -10 -3 L -3 -3 Z',
                    fillColor: isHighlighted ? '#FACC15' : color,
                    fillOpacity: isHighlighted ? 1 : 0.9,
                    strokeColor: isHighlighted ? '#0D1B2A' : '#FFFFFF',
                    strokeWeight: isHighlighted ? 3 : 1.5,
                    scale: isHighlighted ? 1.8 : 1.2,
                  }}
                  zIndex={isHighlighted ? 998 : 1}
                />
              );
            })}

            {/* #NearbyMarkers — InfoWindow para servicios cercanos / emergencias */}
            {selectedService && (() => {
              const { svc, type } = selectedService;
              const lat = svc.latitude ?? svc.lat ?? svc.gps_location?.lat;
              const lng = svc.longitude ?? svc.lng ?? svc.gps_location?.lng;
              if (lat == null || lng == null) return null;
              return (
                <InfoWindow
                  position={{ lat, lng }}
                  onCloseClick={() => setSelectedService(null)}
                >
                  <div className="text-sm min-w-[200px]" style={{ paddingRight: 4 }}>
                    <p className="font-bold text-soem-navy mb-1">{svc.name}</p>
                    <p className="text-xs text-slate-500">
                      {type === 'emergency'
                        ? `🚨 ${String(svc.institution_type || '').replace(/_/g, ' ')}`
                        : `📍 ${(NEARBY_CATEGORIES.find(c => c.id === (svc.category || svc.type))?.label) || 'Servicio cercano'}`}
                    </p>
                    {svc.address && (
                      <p className="text-xs text-slate-500 mt-1">{svc.address}</p>
                    )}
                    {svc.phone && (
                      <p className="text-xs text-soem-primary mt-1">
                        <Phone size={9} className="inline mr-1" />{svc.phone}
                      </p>
                    )}
                    {(svc.distance ?? svc.distance_m) ? (
                      <p className="text-[10px] text-slate-400 mt-1">
                        Distancia: {(((svc.distance ?? svc.distance_m) || 0) / 1000).toFixed(2)} km
                      </p>
                    ) : null}
                  </div>
                </InfoWindow>
              );
            })()}
            {/* #ClickToPlace — InfoWindow al click en marker con
                nombre, categoría, notas, y botones Editar/Eliminar */}
            {selectedMarker && (
              <InfoWindow
                position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div className="text-sm min-w-[200px]" style={{ paddingRight: 4 }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span style={{ fontSize: 18 }}>
                      {getCategoryMeta(selectedMarker.category).icon}
                    </span>
                    <strong className="text-soem-navy">
                      {selectedMarker.name || getCategoryMeta(selectedMarker.category).label}
                    </strong>
                  </div>
                  <p className="text-xs text-slate-500 mb-1">
                    {getCategoryMeta(selectedMarker.category).label}
                    {' · '}
                    {selectedMarker._scope === 'event' ? '⭐ Solo este evento' : '📍 Del venue'}
                  </p>
                  {selectedMarker.notes && (
                    <p className="text-xs text-slate-600 mb-2 italic">{selectedMarker.notes}</p>
                  )}
                  <p className="text-[10px] text-slate-400 font-mono mb-2">
                    {selectedMarker.lat?.toFixed(6)}, {selectedMarker.lng?.toFixed(6)}
                  </p>
                  {((selectedMarker._scope === 'event' && canEditEventExtras) ||
                    (selectedMarker._scope !== 'event' && canEditLocation && mode === 'location')) && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setMapPointModal({ scope: selectedMarker._scope === 'event' ? 'event' : 'location', point: selectedMarker }); setSelectedMarker(null); }}
                        className="text-xs text-soem-primary hover:underline"
                      >Editar</button>
                      <button
                        onClick={() => { handleDeleteMapPoint(selectedMarker); setSelectedMarker(null); }}
                        className="text-xs text-red-600 hover:underline"
                      >Eliminar</button>
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-sm h-64 flex items-center justify-center">
          <p className="text-slate-400 text-sm">Cargando mapa...</p>
        </div>
      )}

      {/* Map Points */}
      <SectionHeader
        icon={MapPin}
        title="Puntos del mapa"
        count={markers.length}
        addLabel={mode === 'event' ? 'Agregar al evento' : 'Agregar al venue'}
        onAdd={() => setMapPointModal({ scope: mode === 'event' ? 'event' : 'location' })}
        canEdit={mode === 'event' ? canEditEventExtras : canEditLocation}
      />
      {markers.length === 0 ? (
        <p className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded-sm">Sin puntos en el mapa todavía.</p>
      ) : (
        <div className="space-y-1.5">
          {markers.map(p => {
            const cfg = getCategoryMeta(p.category);
            return (
              <div key={`${p._scope}-${p.id}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm px-3 py-2 hover:bg-slate-50">
                <span style={{ fontSize: 18 }}>{cfg.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-soem-navy truncate">
                      {p.name || cfg.label}
                    </p>
                    {p._scope === 'event' ? <EventExtraBadge /> : <InheritedBadge />}
                  </div>
                  {p.notes && <p className="text-xs text-slate-400 mt-0.5 truncate">{p.notes}</p>}
                  <p className="text-[10px] text-slate-300 font-mono">{p.lat?.toFixed(4)}, {p.lng?.toFixed(4)}</p>
                </div>
                {((p._scope === 'event' && canEditEventExtras) || (p._scope !== 'event' && canEditLocation && mode === 'location')) && (
                  <button onClick={() => handleDeleteMapPoint(p)}
                    className="text-slate-400 hover:text-red-600">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Nearby Services */}
      <SectionHeader
        icon={Hospital}
        title="Servicios cercanos"
        count={(locData.nearby_services?.length || 0) + (mode === 'event' ? (eventExtras.nearby_services?.length || 0) : 0)}
        addLabel="Buscar y agregar"
        onAdd={mode === 'location' ? () => setNearbySearchModal(true) : undefined}
        canEdit={mode === 'location' && canEditLocation}
      />
      {(locData.nearby_services?.length === 0 && (mode !== 'event' || eventExtras.nearby_services?.length === 0)) ? (
        <p className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded-sm">
          Sin servicios cercanos. {mode === 'location' && canEditLocation ? 'Usa "Buscar y agregar" para encontrar hospitales, gasolineras, hoteles, bancos y restaurantes cercanos.' : ''}
        </p>
      ) : (
        <div className="space-y-1.5">
          {(locData.nearby_services || []).map(svc => {
            const catKey = svc.category || svc.type || 'other';
            const catCfg = NEARBY_CATEGORIES.find(c => c.id === catKey);
            const Ico = catCfg?.icon || MapPin;
            // #NearbyMarkers — hover/click en la fila resalta el marker
            // amarillo. Click también abre InfoWindow del marker.
            const isActive =
              highlightedService?.type === 'nearby' && highlightedService?.id === svc.id;
            return (
              <div
                key={`loc-${svc.id}`}
                className={`flex items-center gap-3 border rounded-sm px-3 py-2 cursor-pointer transition-colors ${
                  isActive
                    ? 'bg-yellow-50 border-yellow-400 shadow-sm'
                    : 'bg-white border-slate-200 hover:bg-slate-50'
                }`}
                onMouseEnter={() => setHighlightedService({ type: 'nearby', id: svc.id })}
                onMouseLeave={() => setHighlightedService(null)}
                onClick={() => setSelectedService({ svc, type: 'nearby' })}
              >
                <Ico size={18} color={catCfg?.color || '#64748B'} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-soem-navy truncate">{svc.name}</p>
                    {mode === 'event' && <InheritedBadge />}
                  </div>
                  <p className="text-xs text-slate-400 truncate">
                    {svc.address || ''}
                    {svc.distance ? ` · ${(svc.distance / 1000).toFixed(1)} km` : (svc.distance_m ? ` · ${(svc.distance_m / 1000).toFixed(1)} km` : '')}
                  </p>
                  {svc.phone && (
                    <p className="text-xs text-soem-primary"><Phone size={9} className="inline mr-1" />{svc.phone}</p>
                  )}
                </div>
                {mode === 'location' && canEditLocation && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteNearby(svc, 'location'); }}
                    className="text-slate-400 hover:text-red-600">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
          {mode === 'event' && (eventExtras.nearby_services || []).map(svc => {
            const catKey = svc.category || svc.type || 'other';
            const catCfg = NEARBY_CATEGORIES.find(c => c.id === catKey);
            const Ico = catCfg?.icon || MapPin;
            return (
              <div key={`ev-${svc.id}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm px-3 py-2 hover:bg-slate-50">
                <Ico size={18} color={catCfg?.color || '#64748B'} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-soem-navy truncate">{svc.name}</p>
                    <EventExtraBadge />
                  </div>
                  <p className="text-xs text-slate-400 truncate">
                    {svc.address || ''}
                    {svc.distance ? ` · ${(svc.distance / 1000).toFixed(1)} km` : ''}
                  </p>
                  {svc.phone && (
                    <p className="text-xs text-soem-primary"><Phone size={9} className="inline mr-1" />{svc.phone}</p>
                  )}
                </div>
                {canEditEventExtras && (
                  <button onClick={() => handleDeleteNearby(svc, 'event')}
                    className="text-slate-400 hover:text-red-600">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Emergency Services */}
      <SectionHeader
        icon={AlertTriangle}
        title="Servicios de emergencia vinculados"
        count={(locData.emergency_services?.length || 0) + (mode === 'event' ? (eventExtras.emergency_services?.length || 0) : 0)}
        addLabel="Vincular"
        onAdd={mode === 'location' ? () => setEmergencyModal(true) : undefined}
        canEdit={mode === 'location' && canEditLocation}
      />
      {(locData.emergency_services?.length === 0 && (mode !== 'event' || eventExtras.emergency_services?.length === 0)) ? (
        <p className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded-sm">Sin servicios vinculados.</p>
      ) : (
        <div className="space-y-1.5">
          {(locData.emergency_services || []).map(svc => {
            const ec = EMERGENCY_ICONS[svc.institution_type] || { icon: AlertTriangle, color: '#64748B' };
            const Ico = ec.icon;
            const hasCoords = (svc.latitude ?? svc.lat ?? svc.gps_location?.lat) != null;
            const isActive =
              highlightedService?.type === 'emergency' && highlightedService?.id === svc.id;
            return (
              <div
                key={`loc-${svc.id}`}
                className={`flex items-center gap-3 border rounded-sm px-3 py-2 transition-colors ${
                  hasCoords ? 'cursor-pointer' : ''
                } ${
                  isActive
                    ? 'bg-yellow-50 border-yellow-400 shadow-sm'
                    : 'bg-white border-slate-200 hover:bg-slate-50'
                }`}
                onMouseEnter={() => hasCoords && setHighlightedService({ type: 'emergency', id: svc.id })}
                onMouseLeave={() => setHighlightedService(null)}
                onClick={() => hasCoords && setSelectedService({ svc, type: 'emergency' })}
              >
                <Ico size={18} color={ec.color} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-soem-navy truncate">{svc.name}</p>
                    {mode === 'event' && <InheritedBadge />}
                    {!hasCoords && (
                      <span className="text-[10px] text-slate-400 italic">sin GPS</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400">
                    {svc.institution_type}
                    {svc.estimated_response_time ? ` · ${svc.estimated_response_time} min` : ''}
                  </p>
                  {svc.phone && (
                    <p className="text-xs text-soem-primary"><Phone size={9} className="inline mr-1" />{svc.phone}</p>
                  )}
                </div>
                {mode === 'location' && canEditLocation && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleUnlinkEmergency(svc, 'location'); }}
                    className="text-slate-400 hover:text-red-600">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
          {mode === 'event' && (eventExtras.emergency_services || []).map(svc => {
            const ec = EMERGENCY_ICONS[svc.institution_type] || { icon: AlertTriangle, color: '#64748B' };
            const Ico = ec.icon;
            return (
              <div key={`ev-${svc.id}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm px-3 py-2 hover:bg-slate-50">
                <Ico size={18} color={ec.color} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-soem-navy truncate">{svc.name}</p>
                    <EventExtraBadge />
                  </div>
                  <p className="text-xs text-slate-400">{svc.institution_type}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modales */}
      {mapPointModal && (
        <MapPointModal
          point={mapPointModal.point}
          defaultCenter={center}
          onPickPosition={startPlacement}
          onSave={handleCreateMapPoint}
          onClose={() => setMapPointModal(null)}
        />
      )}
      {emergencyModal && (
        <EmergencyLinkModal
          onLink={handleLinkEmergency}
          onClose={() => setEmergencyModal(false)}
          currentlyLinkedIds={(locData.emergency_services || []).map(s => s.id)}
        />
      )}
      {/* #UbicacionUnify — modal Google Places para agregar servicios */}
      {nearbySearchModal && (
        <NearbyServicesSearchModal
          locationId={locationId}
          hasGps={hasVenueGps}
          getMapCenter={() => {
            const c = mapRef.current?.getCenter?.();
            return c ? { lat: c.lat(), lng: c.lng() } : center;
          }}
          currentlySaved={locData.nearby_services || []}
          onClose={() => setNearbySearchModal(false)}
          onSaved={reload}
        />
      )}
    </div>
  );
}
